import React, { useEffect, useRef, useState } from 'react';
import { toSrt, downloadFile } from '../../utils/subtitleExport';
import { bunches as toBunches, currentSubtitleIndexAt, endOf, windowOfAnnotations } from '../../utils/timeline';
import Tooltip from '../Tooltip';
import Icon from '../Icon';
import SubtitleRow from './SubtitleRow';
import WordForWordPreview from './WordForWordPreview';
import AnnotationRow from './AnnotationRow';
import AddWordTo, { AddWordButton, targetName } from './AddWordTo';
import { reorderHandlers } from './reorder';
import { formatTime } from './momentTime';
import './SubtitlePanel.css';
import './MomentAnnotator.css';
import './BunchList.css';

/**
 * BunchList — the video's subtitles, each with its annotations under it (a
 * "bunch"), collapsed by default. Annotations that belong to no subtitle are
 * bunched by their start time. Subtitle rows edit and delete; annotation
 * rows edit, delete, align with their subtitle and move to the previous or
 * next subtitle (‹ ›), and up / down among their subtitle's words, the
 * order students see them in (Requirement 58). Each bunch has a "+" to
 * annotate another word of it (Requirement 57).
 *
 * Props:
 *   videoId, currentTime, language, yasna, yasnaError
 *   subtitles, annotations, loading — from useVideoContent
 *   onSubtitleSelect(subtitle), onSubtitleEdited(updated, moved), onSubtitleDeleted(id)
 *   onAnnotationSaved(annotation), onAnnotationUpdated(annotation), onAnnotationDeleted(id)
 *   showExport — show the SRT export button (hidden by default, Requirement 32)
 *   moveControl — optional: the section's up / down arrows (AnnotatePage)
 */
function BunchList({
  videoId, currentTime, language, yasna, yasnaError, subtitles = [], annotations = [], loading = false,
  onSubtitleSelect, onSubtitleEdited, onSubtitleDeleted, onAnnotationSaved, onAnnotationUpdated, onAnnotationDeleted,
  showExport = false, moveControl,
}) {
  const [collapsed, setCollapsed] = useState(true);
  // The bunch a word is being added to: its key and window.
  const [adding, setAdding] = useState(null);
  const activeRef = useRef(null);

  const list = toBunches(subtitles, annotations);
  const current = subtitles[currentSubtitleIndexAt(currentTime, subtitles)];

  // Keep the bunch showing now in view.
  useEffect(() => {
    if (activeRef.current && activeRef.current.scrollIntoView) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [current && current.id, collapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!videoId) return null;

  const counts = [
    subtitles.length > 0 && `${subtitles.length} subtitle${subtitles.length === 1 ? '' : 's'}`,
    annotations.length > 0 && `${annotations.length} word${annotations.length === 1 ? '' : 's'}`,
  ].filter(Boolean).join(' · ');

  return (
    <div className="bunch-list">
      <div className="subtitle-panel-header">
        {/* The whole title toggles the list; the arrow button is its keyboard
            and screen-reader control (its click bubbles up to here). */}
        <div className="subtitle-panel-header-left list-toggle-row" onClick={() => setCollapsed(!collapsed)}>
          <h3 className="subtitle-panel-title">Subtitles &amp; annotations</h3>
          {counts && <span className="bunch-list-count">{counts}</span>}
          <button
            type="button"
            className="subtitle-panel-toggle"
            aria-label={collapsed ? 'Expand subtitles and annotations' : 'Collapse subtitles and annotations'}
            aria-expanded={!collapsed}
          >
            <Icon name={collapsed ? 'chevron-down' : 'chevron-up'} size={16} />
          </button>
        </div>
        <div className="subtitle-panel-header-right">
          {showExport && (
            <Tooltip label="Export subtitles (SRT)">
              <button
                className="subtitle-panel-export-btn"
                onClick={() => downloadFile(`subtitles-${videoId}.srt`, toSrt(subtitles), 'application/x-subrip')}
                disabled={subtitles.length === 0}
                aria-label="Export subtitles as SRT"
              ><Icon name="download" size={14} /> SRT</button>
            </Tooltip>
          )}
          {moveControl}
        </div>
      </div>

      {loading && <p className="subtitle-panel-loading">Loading…</p>}

      {!collapsed && !loading && list.length === 0 && (
        <p className="subtitle-panel-empty">No subtitles or annotations yet. Add them above.</p>
      )}

      {!collapsed && list.length > 0 && (
        <ol className="bunch-items" aria-label="Subtitles and annotations">
          {list.map((b) => {
            const active = !!(b.subtitle && current && b.subtitle.id === current.id);
            const target = b.subtitle
              ? { start: b.subtitle.startTime, end: endOf(b.subtitle), subtitle: true }
              : { ...windowOfAnnotations(b.annotations), subtitle: false };
            const isAdding = !!(adding && adding.key === b.key);
            const moves = reorderHandlers(b.annotations, onAnnotationUpdated);
            const plus = (
              <AddWordButton what={targetName(target)} disabled={isAdding}
                onClick={() => setAdding({ key: b.key, target })} />
            );
            return (
              <li key={b.key} className={`bunch ${active ? 'bunch--active' : ''}`} ref={active ? activeRef : null}>
                {b.subtitle ? (
                  <>
                    <SubtitleRow
                      subtitle={b.subtitle}
                      active={active}
                      currentTime={currentTime}
                      onSelect={onSubtitleSelect}
                      onEdited={onSubtitleEdited}
                      onDeleted={onSubtitleDeleted}
                      actions={plus}
                    />
                    {/* Its gaps annotate a word, or add its gloss in yasna
                        (Requirement 63); shown once the subtitle has words */}
                    <WordForWordPreview subtitle={b.subtitle} annotations={b.annotations} yasna={yasna}
                      language={language} always={b.annotations.length > 0}
                      onAnnotateWord={(text) => setAdding({ key: b.key, target, request: { text, n: Date.now() } })} />
                  </>
                ) : (
                  <div className="bunch-loose">
                    <span className="subtitle-list-time">{formatTime(b.startTime)}</span>
                    <span>No subtitle</span>
                    <span className="bunch-loose-actions">{plus}</span>
                  </div>
                )}
                {b.annotations.length > 0 && (
                  <ul className="moment-annotator-list bunch-annotations"
                    aria-label={b.subtitle ? `Annotations of the subtitle at ${formatTime(b.subtitle.startTime)}` : `Annotations at ${formatTime(b.startTime)}`}>
                    {b.annotations.map((a) => (
                      <AnnotationRow
                        key={a.id}
                        annotation={a}
                        yasna={yasna}
                        language={language}
                        currentTime={currentTime}
                        onUpdated={onAnnotationUpdated}
                        onDeleted={onAnnotationDeleted}
                        reorder={b.annotations.length > 1 ? moves[a.id] : undefined}
                      />
                    ))}
                  </ul>
                )}
                {isAdding && (
                  <AddWordTo
                    target={adding.target}
                    videoId={videoId}
                    language={language}
                    yasna={yasna}
                    yasnaError={yasnaError}
                    onSaved={onAnnotationSaved}
                    onClose={() => setAdding(null)}
                    idSuffix={b.key}
                    request={adding.request}
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export default BunchList;
